// src/pages/internal/clientesSwap/index.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert, Box, Grid, Paper, Snackbar, useTheme, Button, Collapse,
    Typography, Divider, TextField, InputAdornment, IconButton, Pagination,
    Drawer, useMediaQuery,
    Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
    FormControlLabel, Checkbox, Fade
} from "@mui/material";
import { UnfoldMore, UnfoldLess, Add, FilterAltOutlined, Search, Clear } from "@mui/icons-material";
import { useBlocker } from "react-router-dom";
import { useNavigate, useParams } from "react-router-dom";
import useStyles from "./styles";
import LoaderThreeDots from "../../../components/loadingAnimation";
import CardSelection from "./cardSelection";
import InformationMale from "../../../components/artsPersonComponent/informationMale";
import { ServiceItem } from "./serviceItem";
import api from "../../../services/api";
import FilterBar from "../../../components/topBarFilter";
import InternoModalDeleteCliente from "../../../components/modalSaindoDaPagina";

export default function ClientesSwap() {
    const theme = useTheme();
    const styles = useStyles(theme);
    const navigate = useNavigate();
    const { id: idFromUrl } = useParams();

    // --- RESPONSIVIDADE ---
    const isMobile = useMediaQuery("(max-width:800px)");

    const [selectCliente, setSelectCliente] = useState("");
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);
    const [trechos, setTrechos] = useState([]);
    const [trecho, setTrecho] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleteLoad, setDeleteLoad] = useState(false);
    const [forms, setForms] = useState([]);
    const [snackbar, setSnackbar] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [openFilter, setOpenFilter] = useState(false);
    const [buscarTrecho, setBuscarTrecho] = useState("");
    const [expandedIds, setExpandedIds] = useState([]);
    const [tiposDeSwap, setTiposDeSwap] = useState({});
    const [createMode, setCreateMode] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    // Loading dedicado à troca/abertura de um trecho. Mantido como `true` por
    // um mínimo de tempo (`MIN_SELECTION_LOADING_MS`) para evitar piscadas
    // entre estado vazio / loader / conteúdo quando a resposta volta rápido.
    const [selectionLoading, setSelectionLoading] = useState(false);

    // --- ESTADOS PROMOVER / REBAIXAR (parentId) ---
    const [parentChangeLoading, setParentChangeLoading] = useState(false);
    const [demoteDialog, setDemoteDialog] = useState({ open: false, targetId: null, selectedParent: null });
    const [deleteChildren, setDeleteChildren] = useState(true);
    const [duplicateDialog, setDuplicateDialog] = useState({ open: false, targetId: null, includeSubclientes: true });

    // --- CONTROLE DA ABA ATIVA ---
    const [activeTab, setActiveTab] = useState(0);

    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    // --- ESTADOS PARA VERIFICAÇÃO DE SAÍDA ---
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [openExitModal, setOpenExitModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);

    // =========================================================================
    // LÓGICA DE INTERCEPTAÇÃO DE NAVEGAÇÃO (INTERNA E EXTERNA)
    // =========================================================================

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        if (blocker.state === "blocked") {
            setPendingNavigation(() => () => blocker.proceed());
            setOpenExitModal(true);
        }
    }, [blocker.state]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const handleNavigation = (action) => {
        if (hasUnsavedChanges) {
            setPendingNavigation(() => action);
            setOpenExitModal(true);
        } else {
            action();
        }
    };

    const confirmExit = () => {
        setHasUnsavedChanges(false);
        setOpenExitModal(false);
        if (pendingNavigation) pendingNavigation();
    };

    const cancelExit = () => {
        setOpenExitModal(false);
        setPendingNavigation(null);
        if (blocker.state === "blocked") {
            blocker.reset();
        }
    };

    // Ação acionada ao tentar fechar o Drawer no Mobile
    const handleCloseDrawer = () => {
        handleNavigation(() => {
            setSelectCliente("");
            setCreateMode(false);
            setEditMode(false);
        });
    };
    // =========================================================================

    async function hanldleCreateTrecho(payload) {
        try {
            setCreateLoading(true);
            const response = await api.post("/interno/create/clientesSwap", payload);

            setSnackbar({ severity: "success", children: "Trecho criado com sucesso" });
            setHasUnsavedChanges(false);

            await getTrechos();
            setCreateMode(false);

            const novoId = response.data?.id || response.data?.clienteSwap?.id;
            if (novoId) {
                setSelectCliente(novoId);
            }
        } catch (error) {
            console.error(error);
            setSnackbar({ severity: "error", children: "Erro ao criar o trecho. Tente novamente mais tarde." });
        } finally {
            setCreateLoading(false);
        }
    }

    async function handleUpdateTrecho(payload) {
        const id = payload?.clienteSwap?.id;

        if (!id) {
            setSnackbar({ severity: "error", children: "ID do trecho ausente no payload de atualização." });
            return;
        }

        try {
            setUpdateLoading(true);
            await api.put("/interno/update/clientesSwap", payload);

            setSnackbar({ severity: "success", children: "Trecho atualizado com sucesso" });
            setHasUnsavedChanges(false);

            await getTrechos();
            await getTrechoById(selectCliente);
            setEditMode(false);
        } catch (error) {
            console.error("Erro ao atualizar trecho:", error);
            setSnackbar({ severity: "error", children: "Erro ao atualizar o trecho. Tente novamente mais tarde." });
        } finally {
            setUpdateLoading(false);
        }
    }

    const normalizeText = (text) =>
        (text || "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const buscaNormalizada = normalizeText(buscarTrecho);

    const updateForm = useCallback((trechoId, updatedForm, tempId) => {
        setForms((prev) => {
            const newForms = { ...prev };
            const updatedForms = newForms[trechoId];
            return newForms;
        });
    }, []);

    useEffect(() => {
        getTrechos();
        getDeliveryOptions();
    }, []);

    // Sincroniza a URL (param :id) -> seleção, validando contra a lista carregada.
    const urlValidatedRef = useRef(false);
    useEffect(() => {
        if (urlValidatedRef.current) return;
        if (!trechos || trechos.length === 0) return;
        urlValidatedRef.current = true;

        if (!idFromUrl) return;

        const parent = trechos.find(
            (t) => t.id === idFromUrl || t.subclientes?.some((s) => s.id === idFromUrl)
        );
        if (parent) {
            setSelectCliente(idFromUrl);
            // Se o id corresponde a um subtrecho, expande o trecho pai automaticamente.
            if (parent.id !== idFromUrl) {
                setExpandedIds((prev) => (prev.includes(parent.id) ? prev : [...prev, parent.id]));
            }
        } else {
            setSnackbar({ severity: "warning", children: "Trecho não encontrado." });
            navigate("/interno/clientesSwap", { replace: true });
        }
    }, [trechos, idFromUrl, navigate]);

    // Sincroniza seleção -> URL (sem disparar re-render do router para evitar flicker).
    useEffect(() => {
        if (!urlValidatedRef.current) return;
        const targetPath = selectCliente
            ? `/interno/clientesSwap/${selectCliente}`
            : "/interno/clientesSwap";
        if (window.location.pathname !== targetPath) {
            window.history.replaceState(window.history.state, "", targetPath);
        }
    }, [selectCliente]);

    useEffect(() => {
        if (selectCliente) {
            getTrechoById(selectCliente);
        }
    }, [selectCliente]);

    useEffect(() => {
        setPage(1);
    }, [buscaNormalizada]);

    useEffect(() => {
        if (buscaNormalizada.length > 0) {
            const idsParaExpandir = trechos
                .filter(t => t.subclientes?.some(sub =>
                    normalizeText(sub.nome).includes(buscaNormalizada) ||
                    normalizeText(sub.cnl).includes(buscaNormalizada)
                ))
                .map(t => t.id);

            setExpandedIds(prev => [...new Set([...prev, ...idsParaExpandir])]);
        }
    }, [buscaNormalizada, trechos]);

    function base64ToBlob(base64, mimeType) {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    }

    async function getTrechoById(id) {
        const start = Date.now();
        setSelectionLoading(true);
        try {
            const response = await api.post("/interno/find/clientesSwap", { id });
            let trechoData = response.data;

            if (trechoData.clientesSwapAnexos && trechoData.clientesSwapAnexos.length > 0) {
                const anexosConvertidos = trechoData.clientesSwapAnexos.map((anexo) => {
                    if (!anexo.base64) return anexo;

                    const blob = base64ToBlob(anexo.base64, anexo.type);
                    const file = new File([blob], anexo.name, { type: anexo.type });

                    return {
                        id: anexo.id,
                        originalFile: file,
                        name: anexo.name,
                        type: anexo.type,
                        preview: URL.createObjectURL(file),
                        isPdf: anexo.isPdf
                    };
                });

                trechoData.clientesSwapAnexos = anexosConvertidos;
            }

            setTrecho(trechoData);

        } catch (error) {
            console.error(error);
            setSnackbar({ severity: "error", children: "Erro ao buscar o trecho." });
        } finally {
            // Mantém o loader visível por um tempo mínimo para evitar flicker em
            // respostas rápidas (cache/local). Animado via <Fade/>.
            const MIN_SELECTION_LOADING_MS = 350;
            const elapsed = Date.now() - start;
            if (elapsed < MIN_SELECTION_LOADING_MS) {
                await new Promise((r) => setTimeout(r, MIN_SELECTION_LOADING_MS - elapsed));
            }
            setSelectionLoading(false);
        }
    }

    async function getTrechos() {
        try {
            setLoading(true);
            const response = await api.get("/interno/findMany/clientesSwap");
            setTrechos(response.data);
        } catch (error) {
            console.error(error);
            setSnackbar({ severity: "error", children: "Erro ao buscar os trechos." });
        } finally {
            setLoading(false);
        }
    }

    async function getDeliveryOptions() {
        try {
            const response = await api.post("/interno/findMany/opcaoCircuito");
            const arrayDeOpcoes = Array.isArray(response.data) ? response.data : (response?.data?.swapTypes || []);

            setTiposDeSwap({
                swapTypes: arrayDeOpcoes,
            });
        } catch (error) {
            console.error(error);
            setSnackbar({ children: "Error: Falha ao obter opções", severity: "error" });
        }
    }

    async function handleDeleteById() {
        try {
            setDeleteLoad(true);
            await api.post(`/interno/delete/clientesSwap`, { id: idToDelete, deleteChildren });
            getTrechos();
            setOpenDeleteModal(false);

            const wasSelectedOrChild =
                selectCliente === idToDelete ||
                (deleteChildren && trechoToDelete?.subclientes?.some((s) => s.id === selectCliente));
            if (wasSelectedOrChild) {
                setSelectCliente("");
                setTrecho(null);
            }

            setIdToDelete(null);
            setDeleteChildren(true);

            setSnackbar({ severity: "success", children: "Trecho excluído com sucesso" });
        } catch (error) {
            console.error(error);
            setSnackbar({ severity: "error", children: "Erro ao excluir o trecho." });
        } finally {
            setDeleteLoad(false);
        }
    }

    const handleOpenDeleteModal = (id) => {
        setIdToDelete(id);
        setDeleteChildren(true);
        setOpenDeleteModal(true);
    };

    const trechoToDelete = trechos.find((t) => t.id === idToDelete);
    const deleteHasChildren = (trechoToDelete?.subclientes?.length || 0) > 0;

    async function changeTrechoParent(id, parentId) {
        try {
            setParentChangeLoading(true);
            await api.patch("/interno/clientesSwap/parent", { id, parentId });
            await getTrechos();
            if (selectCliente) {
                await getTrechoById(selectCliente);
            }
            setSnackbar({
                severity: "success",
                children: parentId ? "Trecho rebaixado a subtrecho com sucesso" : "Subtrecho promovido a trecho com sucesso",
            });
            return true;
        } catch (error) {
            console.error(error);
            const msg = error?.response?.data?.error || "Erro ao alterar a hierarquia do trecho.";
            setSnackbar({ severity: "error", children: msg });
            return false;
        } finally {
            setParentChangeLoading(false);
        }
    }

    const handlePromoteSubtrecho = (id) => {
        handleNavigation(() => changeTrechoParent(id, null));
    };

    const handleOpenDemoteDialog = (id) => {
        handleNavigation(() => setDemoteDialog({ open: true, targetId: id, selectedParent: null }));
    };

    const handleDuplicateTrecho = (id) => {
        const trechoAlvo = trechos.find((t) => t.id === id);
        const temSubtrechos = !!(trechoAlvo?.subclientes && trechoAlvo.subclientes.length > 0);
        if (temSubtrechos) {
            handleNavigation(() => setDuplicateDialog({ open: true, targetId: id, includeSubclientes: true }));
            return;
        }
        executeDuplicateTrecho(id, false);
    };

    const closeDuplicateDialog = () => setDuplicateDialog({ open: false, targetId: null, includeSubclientes: true });

    const executeDuplicateTrecho = (id, includeSubclientes) => {
        handleNavigation(async () => {
            try {
                setParentChangeLoading(true);
                const response = await api.post("/interno/clientesSwap/clone", { id, includeSubclientes });
                await getTrechos();
                const novoId = response.data?.id;
                if (novoId) setSelectCliente(novoId);
                setSnackbar({ severity: "success", children: "Trecho duplicado com sucesso" });
            } catch (error) {
                console.error(error);
                const msg = error?.response?.data?.error || "Erro ao duplicar o trecho.";
                setSnackbar({ severity: "error", children: msg });
            } finally {
                setParentChangeLoading(false);
            }
        });
    };

    const handleConfirmDuplicate = () => {
        if (!duplicateDialog.targetId) return;
        const { targetId, includeSubclientes } = duplicateDialog;
        closeDuplicateDialog();
        executeDuplicateTrecho(targetId, includeSubclientes);
    };

    const closeDemoteDialog = () => setDemoteDialog({ open: false, targetId: null, selectedParent: null });

    const handleConfirmDemote = async () => {
        if (!demoteDialog.targetId || !demoteDialog.selectedParent?.id) return;
        const ok = await changeTrechoParent(demoteDialog.targetId, demoteDialog.selectedParent.id);
        if (ok) closeDemoteDialog();
    };

    const demoteParentOptions = trechos.filter(
        (t) => !t.parentId && t.id !== demoteDialog.targetId
    );

    const handleToggleExpand = (id, e) => {
        e.stopPropagation();
        e.preventDefault();
        setExpandedIds((prev) =>
            prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
        );
    };

    const parentsWithChildrenIds = trechos
        .filter((t) => t?.subclientes && t.subclientes.length > 0)
        .map((t) => t.id);

    const allExpanded =
        parentsWithChildrenIds.length > 0 &&
        parentsWithChildrenIds.every((id) => expandedIds.includes(id));

    const handleToggleAll = () => {
        if (allExpanded) {
            setExpandedIds([]);
        } else {
            setExpandedIds(parentsWithChildrenIds);
        }
    };

    const actions = [
        {
            icon: <FilterAltOutlined />,
            name: "Filtro",
            onClick: () => setOpenFilter(true),
        },
        {
            icon: <Add />,
            name: "Novo Trecho",
            onClick: () => handleNavigation(() => setCreateMode(true)),
        },
    ];

    const filteredTrechos = trechos.filter((trecho) => {
        const paiCorresponde =
            normalizeText(trecho.nome).includes(buscaNormalizada) ||
            normalizeText(trecho.cnl).includes(buscaNormalizada);

        const algumFilhoCorresponde = trecho.subclientes?.some((sub) =>
            normalizeText(sub.nome).includes(buscaNormalizada) ||
            normalizeText(sub.cnl).includes(buscaNormalizada)
        );

        return paiCorresponde || algumFilhoCorresponde;
    });

    const totalPages = Math.ceil(filteredTrechos.length / itemsPerPage);
    const paginatedTrechos = filteredTrechos.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    // Vai automaticamente para a página do trecho selecionado quando a seleção
    // mudar (ex: criação/duplicação). Não interfere na navegação manual de páginas.
    const lastJumpedSelectionRef = useRef(null);
    useEffect(() => {
        if (!selectCliente) {
            lastJumpedSelectionRef.current = null;
            return;
        }
        if (lastJumpedSelectionRef.current === selectCliente) return;
        if (filteredTrechos.length === 0) return;
        const parentIndex = filteredTrechos.findIndex(
            (t) => t.id === selectCliente || t.subclientes?.some((s) => s.id === selectCliente)
        );
        if (parentIndex < 0) return;
        const targetPage = Math.floor(parentIndex / itemsPerPage) + 1;
        lastJumpedSelectionRef.current = selectCliente;
        if (targetPage !== page) setPage(targetPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectCliente, filteredTrechos]);

    // =========================================================================
    // RENDERIZAÇÃO DO CONTEÚDO DA DIREITA (USADO NO GRID OU DRAWER)
    // =========================================================================
    const renderServiceContent = () => {
        if (createMode) {
            return (
                <ServiceItem
                    key="create-mode-key"
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    trecho={null}
                    selectedTitle={"Novo Trecho"}
                    forms={forms}
                    setForms={setForms}
                    updateForm={updateForm}
                    setSnackbar={setSnackbar}
                    setEditMode={setEditMode}
                    tiposDeSwap={tiposDeSwap}
                    updateOptions={getDeliveryOptions}
                    editMode={editMode}
                    createMode={createMode}
                    setCreateMode={setCreateMode}
                    onCreate={hanldleCreateTrecho}
                    externalLoading={createLoading}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                />
            );
        }

        if (selectCliente) {
            return (
                <ServiceItem
                    key={`view-edit-${selectCliente}`}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    trecho={trecho}
                    selectedTitle={trecho?.nome}
                    selectedSubTitle={trecho?.cnl}
                    forms={forms}
                    setForms={setForms}
                    updateForm={updateForm}
                    setSnackbar={setSnackbar}
                    setEditMode={setEditMode}
                    tiposDeSwap={tiposDeSwap}
                    updateOptions={getDeliveryOptions}
                    editMode={editMode}
                    onUpdate={handleUpdateTrecho}
                    externalLoading={updateLoading}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                />
            );
        }

        return null;
    };

    return (
        <Box>
            <FilterBar
                actions={actions}
                openFilter={openFilter}
                setOpenFilter={setOpenFilter}
                type={0}
                selectorComponentLeft={
                    <TextField
                        id={"trecho-search"}
                        size="small"
                        label="Buscar Trecho"
                        variant="outlined"
                        value={buscarTrecho}
                        onChange={(event) => setBuscarTrecho(event.target.value)}
                        sx={{ width: isMobile ? "100%" : "400px", maxWidth: "90%" }}
                        autoComplete="off"
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                            endAdornment: buscarTrecho ? (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setBuscarTrecho("")}><Clear fontSize="small" /></IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                }
                selectorComponentRight={

                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button variant="outlined" startIcon={<Add />} onClick={() => {
                            handleNavigation(() => {
                                setCreateMode(true);
                                setSelectCliente("");
                                setTrecho(null);
                                setForms([]);
                                setEditMode(false);
                                setActiveTab(0);
                            });
                        }}>
                            {isMobile ? "Novo" : "Novo Trecho"}
                        </Button>
                    </Box>

                }
            />

            <Box sx={styles.paper}>
                <Grid container spacing={1} columns={{ xs: 8, sm: 8, smx: 8, md: 12, lg: 12, xl: 12, xxl: 12 }}>
                    <Grid sx={styles.paperFluxo} item xs={8} sm={8} smx={isMobile ? 8 : 3} md={4} lg={4}>
                        <Paper elevation={theme.palette.mode === "dark" ? 0 : 3} sx={{ ...styles.paperFluxo, height: "83vh", overflowY: "auto", display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, p: 1 }}>
                                <Typography variant="subtitle2" fontSize={"18px"}>Trechos</Typography>

                                {parentsWithChildrenIds.length > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={handleToggleAll}
                                            startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
                                            variant="outlined"
                                        >
                                            {allExpanded ? "Recolher Todos" : "Expandir Todos"}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                            <Divider />

                            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                                {paginatedTrechos.map((trecho) => {
                                    const isSelected = selectCliente === trecho?.id;
                                    const isExpanded = expandedIds.includes(trecho?.id);
                                    const hasChildren = trecho?.subclientes && trecho.subclientes.length > 0;
                                    const isChildSelected = trecho?.subclientes?.some((subcliente) => subcliente.id === selectCliente);
                                    const trechoAnexosCount = trecho?.anexosCount || 0;
                                    const trechoHasKmz = !!trecho?.hasKmz;

                                    return (
                                        <React.Fragment key={trecho?.id}>
                                            <CardSelection
                                                idTrecho={trecho?.id}
                                                title={trecho?.nome}
                                                subtitle={trecho?.cnl}
                                                selected={isSelected}
                                                childSelected={isChildSelected}
                                                onClick={() => {
                                                    handleNavigation(() => {
                                                        setSelectCliente(isSelected ? "" : trecho?.id);
                                                        setCreateMode(false);
                                                        setEditMode(false);
                                                    });
                                                }}
                                                hasChildren={hasChildren}
                                                isExpanded={isExpanded}
                                                onToggleExpand={(e) => handleToggleExpand(trecho?.id, e)}
                                                onDelete={handleOpenDeleteModal}
                                                onDemote={handleOpenDemoteDialog}
                                                onDuplicate={handleDuplicateTrecho}
                                                anexosCount={trechoAnexosCount}
                                                hasKmz={trechoHasKmz}
                                            />

                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                <Box sx={{ ml: 2.5, display: "flex", flexDirection: "column" }}>
                                                    {trecho.subclientes?.map((subcliente, index) => {
                                                        const isSubSelected = selectCliente === subcliente?.id;
                                                        const isLast = index === trecho.subclientes.length - 1;
                                                        const subAnexosCount = subcliente?.anexosCount || 0;
                                                        const subHasKmz = !!subcliente?.hasKmz;

                                                        return (
                                                            <Box key={subcliente?.id} sx={{ position: 'relative', pl: 2 }}>
                                                                <Box sx={{ position: 'absolute', left: 0, top: index === 0 ? '0px' : 0, bottom: isLast ? '50%' : 0, borderLeft: `3px solid ${theme.palette.colors.green.light}` }} />
                                                                <Box sx={{ position: 'absolute', left: 0, top: '50%', width: '24px', borderTop: `3px solid ${theme.palette.colors.green.light}` }} />

                                                                <CardSelection
                                                                    index={index}
                                                                    idTrecho={subcliente?.id}
                                                                    title={subcliente?.nome}
                                                                    subtitle={subcliente?.cnl}
                                                                    isChild={true}
                                                                    selected={isSubSelected}
                                                                    onClick={() => {
                                                                        handleNavigation(() => {
                                                                            setSelectCliente(isSubSelected ? "" : subcliente?.id);
                                                                            setCreateMode(false);
                                                                            setEditMode(false);
                                                                        });
                                                                    }}
                                                                    onDelete={handleOpenDeleteModal}
                                                                    onPromote={handlePromoteSubtrecho}
                                                                    onDuplicate={handleDuplicateTrecho}
                                                                    anexosCount={subAnexosCount}
                                                                    hasKmz={subHasKmz}
                                                                />
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Collapse>
                                        </React.Fragment>
                                    );
                                })}
                            </Box>

                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, borderTop: `1px solid ${theme.palette.divider}`, mt: 'auto' }}>
                                    <Pagination count={totalPages} page={page} onChange={(event, value) => setPage(value)} color="primary" size="small" siblingCount={0} />
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* RENDERIZAÇÃO DIREITA (APENAS DESKTOP) */}
                    {!isMobile && (
                        <Grid sx={styles.paperFluxo} item xs={8} sm={8} smx={5} md={8} lg={8}>
                            {(selectCliente || createMode) && <Box sx={styles.boxBlur} />}
                            <Paper sx={{ ...styles.paperFluxo, height: "83vh", pt: 0, position: 'relative', overflow: 'hidden' }}>
                                {(() => {
                                    const showLoader = loading || selectionLoading;
                                    const hasContent = createMode || selectCliente;
                                    const showEmptyState = !showLoader && !hasContent;
                                    const showContent = !showLoader && hasContent;

                                    return (
                                        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                            <Fade in={showLoader} timeout={{ enter: 150, exit: 250 }} unmountOnExit>
                                                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <LoaderThreeDots />
                                                </Box>
                                            </Fade>
                                            <Fade in={showContent} timeout={{ enter: 250, exit: 150 }} unmountOnExit>
                                                <Box sx={{ position: 'absolute', inset: 0, overflowY: 'auto' }}>
                                                    {renderServiceContent()}
                                                </Box>
                                            </Fade>
                                            <Fade in={showEmptyState} timeout={{ enter: 250, exit: 150 }} unmountOnExit>
                                                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <InformationMale
                                                        mainFill={theme.palette.secondary.dark}
                                                        primaryCircleFill={theme.palette.secondary.main}
                                                        secondaryCircleFill={theme.palette.secondary.light}
                                                        title={"Você ainda não selecionou um"}
                                                        highlight={"Trecho"}
                                                        subtitle={"As informações de um trecho aparecerão aqui assim\nque um trecho for escolhido."}
                                                    />
                                                </Box>
                                            </Fade>
                                        </Box>
                                    );
                                })()}
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {/* MODAL PADRÃO DE EXCLUSÃO DE TRECHO */}
                <InternoModalDeleteCliente
                    openConfirmacao={openDeleteModal}
                    setOpenConfirmacao={setOpenDeleteModal}
                    confirmLoad={deleteLoad}
                    execute={handleDeleteById}
                    cancel={() => {
                        setIdToDelete(null);
                        setDeleteChildren(true);
                    }}
                    title="Atenção"
                    contentText="Tem certeza que deseja excluir este trecho? Essa ação é irreversível."
                    confirmText="Excluir"
                >
                    {deleteHasChildren && (
                        <Box sx={{ mt: 1, textAlign: "left" }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Este trecho possui {trechoToDelete.subclientes.length} subtrecho(s). Por padrão, eles também serão excluídos.
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!deleteChildren}
                                        onChange={(e) => setDeleteChildren(!e.target.checked)}
                                    />
                                }
                                label="Manter os subtrechos (promovê-los a trechos de nível superior)"
                            />
                        </Box>
                    )}
                </InternoModalDeleteCliente>

                {/* MODAL PARA REBAIXAR TRECHO A SUBTRECHO */}
                <InternoModalDeleteCliente
                    openConfirmacao={demoteDialog.open}
                    setOpenConfirmacao={(open) => {
                        if (!open) closeDemoteDialog();
                    }}
                    confirmLoad={parentChangeLoading}
                    confirmDisabled={!demoteDialog.selectedParent}
                    execute={handleConfirmDemote}
                    cancel={closeDemoteDialog}
                    severity="info"
                    title="Tornar subtrecho"
                    contentText="Selecione o trecho que será o pai. Apenas trechos de nível superior podem ser escolhidos."
                    confirmText="Confirmar"
                    paperSx={{ width: "480px", maxWidth: "90vw" }}
                    paperInternoSx={{ width: "440px", maxWidth: "85vw" }}
                >
                    <Box sx={{ mt: 1, width: "100%", px: 1 }}>
                        <Autocomplete
                            fullWidth
                            options={demoteParentOptions}
                            getOptionLabel={(opt) => opt?.nome ? `${opt.nome}${opt.cnl ? ` — CNL: ${opt.cnl}` : ""}` : ""}
                            value={demoteDialog.selectedParent}
                            onChange={(_, value) => setDemoteDialog((prev) => ({ ...prev, selectedParent: value }))}
                            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                            renderInput={(params) => (
                                <TextField {...params} label="Trecho pai" size="small" autoFocus fullWidth />
                            )}
                            noOptionsText="Nenhum trecho disponível"
                        />
                    </Box>
                </InternoModalDeleteCliente>

                {/* MODAL DE CONFIRMAÇÃO PARA DUPLICAR COM SUBTRECHOS */}
                <InternoModalDeleteCliente
                    openConfirmacao={duplicateDialog.open}
                    setOpenConfirmacao={(open) => {
                        if (!open) closeDuplicateDialog();
                    }}
                    confirmLoad={parentChangeLoading}
                    execute={handleConfirmDuplicate}
                    cancel={closeDuplicateDialog}
                    severity="info"
                    title="Duplicar trecho"
                    contentText="Este trecho possui subtrechos. Por padrão, eles também serão duplicados."
                    confirmText="Duplicar"
                >
                    <Box sx={{ mt: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!duplicateDialog.includeSubclientes}
                                    onChange={(e) => setDuplicateDialog((prev) => ({ ...prev, includeSubclientes: !e.target.checked }))}
                                />
                            }
                            label="Não duplicar os subtrechos"
                        />
                    </Box>
                </InternoModalDeleteCliente>

                {/* MODAL DE SAÍDA COM ALTERAÇÕES PENDENTES */}
                <InternoModalDeleteCliente
                    openConfirmacao={openExitModal}
                    setOpenConfirmacao={setOpenExitModal}
                    confirmLoad={false}
                    execute={confirmExit}
                    cancel={cancelExit}
                    severity="warning"
                    title="Atenção"
                    contentText="Você está prestes a sair. As alterações não salvas serão perdidas, deseja continuar?"
                    confirmText="Continuar"
                />

                {!!snackbar && (
                    <Snackbar open onClose={() => setSnackbar(null)} autoHideDuration={2500} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                        <Alert {...snackbar} onClose={() => setSnackbar(null)} />
                    </Snackbar>
                )}
            </Box>

            {/* RENDERIZAÇÃO DRAWER (APENAS MOBILE) */}
            <Drawer
                anchor="right"
                open={isMobile && (createMode || Boolean(selectCliente))}
                onClose={handleCloseDrawer}
                PaperProps={{ sx: { width: '100%', maxWidth: '100vw' } }}
            >
                {/* Header do Drawer */}
                <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <IconButton onClick={handleCloseDrawer} edge="start" sx={{ mr: 1 }}>
                        <Clear />
                    </IconButton>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {createMode ? "Novo Trecho" : (trecho?.nome || "Detalhes")}
                    </Typography>
                </Box>

                {/* Corpo do Drawer */}
                <Box sx={{ flexGrow: 1, overflowY: "auto", p: 1, position: 'relative' }}>
                    {(() => {
                        const showLoader = loading || selectionLoading;
                        return (
                            <Box sx={{ position: 'relative', minHeight: '100%' }}>
                                <Fade in={showLoader} timeout={{ enter: 150, exit: 250 }} unmountOnExit>
                                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LoaderThreeDots />
                                    </Box>
                                </Fade>
                                <Fade in={!showLoader} timeout={{ enter: 250, exit: 150 }} unmountOnExit>
                                    <Box>
                                        {renderServiceContent()}
                                    </Box>
                                </Fade>
                            </Box>
                        );
                    })()}
                </Box>
            </Drawer>
        </Box>
    );
}